/**
 * Client API per il backend Sibilla.
 * In sviluppo le chiamate passano per il proxy configurato in setupProxy.js,
 * quindi il base URL e' relativo e non richiede CORS.
 */

import { authHeaders, clearAuth } from './auth';
import { validateWaterReference } from './waterReference';
import { validateMassChoices, validateMassReference } from './massReference';

const BASE_URL = '/api';
const TRANSIENT_GATEWAY_STATUSES = new Set([502, 503, 504, 530]);

export class ApiError extends Error {
  constructor(message, { status, fieldErrors = {} } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.fieldErrors = fieldErrors;
  }
}

export function problemDetailsToError(errorBody, status) {
  const fieldErrors = Object.fromEntries(
    Object.entries(errorBody?.errors || {}).map(([field, messages]) => [
      field,
      Array.isArray(messages) ? messages.filter(Boolean).join(' ') : String(messages || '')
    ])
  );
  const firstFieldError = Object.values(fieldErrors).find(Boolean);
  const fallback = TRANSIENT_GATEWAY_STATUSES.has(status)
    ? 'Servizio Sibilla temporaneamente non disponibile'
    : `Errore ${status}`;
  const genericValidationTitle = errorBody?.title === 'One or more validation errors occurred.';
  const message = errorBody?.error || errorBody?.detail || firstFieldError ||
    (!genericValidationTitle && errorBody?.title) || fallback;
  return new ApiError(message, { status, fieldErrors });
}

async function request(path, options = {}) {
  const url = `${BASE_URL}${path}`;
  const { retryOnNetwork = 0, responseType, ...fetchOptions } = options;
  let response;
  for (let attempt = 0; attempt <= retryOnNetwork; attempt += 1) {
    try {
      response = await fetch(url, {
        ...fetchOptions,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          ...authHeaders(),
          ...fetchOptions.headers
        }
      });
      if (TRANSIENT_GATEWAY_STATUSES.has(response.status) && attempt < retryOnNetwork) {
        await new Promise(resolve => window.setTimeout(resolve, 700 * (attempt + 1)));
        continue;
      }
      break;
    } catch (networkError) {
      // Cancellation is not a connectivity failure and must never be retried.
      if (networkError?.name === 'AbortError') throw networkError;
      if (attempt === retryOnNetwork) {
        throw new Error('Collegamento a Sibilla temporaneamente non disponibile');
      }
      await new Promise(resolve => window.setTimeout(resolve, 700 * (attempt + 1)));
    }
  }

  if (response.status === 401) {
    clearAuth();
    window.dispatchEvent(new Event('sibilla:unauthorized'));
  }

  if (!response.ok) {
    let errorBody;
    try {
      errorBody = await response.json();
    } catch {
      // Il body può essere vuoto o non JSON (ad esempio da un gateway).
    }
    throw problemDetailsToError(errorBody, response.status);
  }

  if (response.status === 204) {
    return null;
  }

  return responseType === 'blob' ? response.blob() : response.json();
}

// Pazienti
export const patientsApi = {
  activity: () => request('/patients/activity'),
  exportArchive: () => request('/patients/export', {responseType:'blob'}),
  getArchive: () => request('/patients?includeInactive=true'),
  getAll: (search) => request(`/patients${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  get: (id) => request(`/patients/${id}`),
  create: (data) => request('/patients', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/patients/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => request(`/patients/${id}`, { method: 'DELETE' })
};

// Dispositivi
export const devicesApi = {
  getAll: () => request('/devices'),
  get: (id) => request(`/devices/${id}`),
  getBySerial: (serialNumber) => request(`/devices/by-serial/${encodeURIComponent(serialNumber)}`),
  create: (data) => request('/devices', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/devices/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  heartbeat: (id, batteryLevel) => request(`/devices/${id}/heartbeat`, {
    method: 'POST',
    body: JSON.stringify({ batteryLevel })
  }),
  delete: (id) => request(`/devices/${id}`, { method: 'DELETE' })
};

// Misurazioni
export const measurementsApi = {
  getMassReference: async (id, branch, ageBasis, signal) => {
    if (typeof id !== 'string' || !id.trim()) throw new Error('Salva la misura prima di richiedere il confronto.');
    validateMassChoices(branch, ageBasis);
    const query = new URLSearchParams({ branch, ageBasis });
    const result = await request(`/measurements/${encodeURIComponent(id)}/mass-reference?${query}`, { signal });
    return validateMassReference(result, id, branch, ageBasis);
  },
  getWaterReference: async (id, allow50KhzFallback = false, signal) => {
    if (typeof id !== 'string' || !id.trim()) throw new Error('Salva la misura prima di richiedere il confronto.');
    const fallback = allow50KhzFallback === true;
    const result = await request(
      `/measurements/${encodeURIComponent(id)}/water-reference?allow50KhzFallback=${fallback}`, { signal });
    return validateWaterReference(result, id, fallback);
  },
  getEcwReference: (id, allow50KhzFallback = false, signal) => request(
    `/measurements/${encodeURIComponent(id)}/ecw-reference?allow50KhzFallback=${allow50KhzFallback === true}`, { signal }),
  get: (id) => request(`/measurements/${id}`),
  getByPatient: (patientId, limit = 50) =>
    request(`/measurements/by-patient/${patientId}?limit=${limit}`),
  create: (data) => request('/measurements', { method: 'POST', body: JSON.stringify(data) })
};

// Visite cliniche
export const visitsApi = {
  getAll: (patientId, limit = 50) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (patientId) params.set('patientId', patientId);
    return request(`/visits?${params}`);
  },
  get: (id) => request(`/visits/${id}`),
  create: (data) => request('/visits', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/visits/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => request(`/visits/${id}`, { method: 'DELETE' })
};

// Sistema Tessera Sanitaria. Le credenziali professionali restano nel backend.
export const sistemaTsApi = {
  health: () => request('/sistema-ts/health'),
  submit: (operation, document, options = {}) => {
    const params = new URLSearchParams({
      environment: options.environment || 'test',
      dryRun: String(options.dryRun !== false),
      confirmProduction: String(options.confirmProduction === true)
    });
    const headers = options.profile ? { 'X-Sistema-TS-Profile': options.profile } : {};
    return request(`/sistema-ts/documents/${operation}?${params}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(document)
    });
  }
};

export const dietPlansApi = {
  getAll: (patientId) => request(`/diet-plans${patientId ? `?patientId=${encodeURIComponent(patientId)}` : ''}`),
  generateDraft: (data) => request('/diet-plans/ai-draft', { method: 'POST', body: JSON.stringify(data), retryOnNetwork: 1 }),
  create: (data) => request('/diet-plans', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/diet-plans/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => request(`/diet-plans/${id}`, { method: 'DELETE' })
};

export const dietProtocolsApi = {
  getAll: () => request('/diet-protocols')
};

export const foodsApi = {
  byIds: (ids) => request('/foods/by-ids', {method:'POST', body:JSON.stringify(ids)}),
  search: (query = '', limit = 20) => request(`/foods?query=${encodeURIComponent(query)}&limit=${limit}`),
  lookupBarcode: (barcode) => request(`/foods/open-food-facts/${encodeURIComponent(barcode)}`),
  importBarcode: (barcode) => request(`/foods/open-food-facts/${encodeURIComponent(barcode)}/import`, { method: 'POST' })
};

export const foodRecipesApi = {
  getAll: (query = '') => request(`/food-recipes${query ? `?query=${encodeURIComponent(query)}` : ''}`),
  create: (data) => request('/food-recipes', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/food-recipes/${id}`, { method: 'PUT', body: JSON.stringify(data) })
};

export const appointmentsApi = {
  calendar: (from, to) => request(`/appointments/calendar?${new URLSearchParams({from, to})}`),
  getAll: (from, to) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    return request(`/appointments${params.toString() ? `?${params}` : ''}`);
  },
  create: (data) => request('/appointments', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/appointments/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => request(`/appointments/${id}`, { method: 'DELETE' })
};

export const adminCustomersApi = {
  getAll: () => request('/admin/customers'),
  get: (id) => request(`/admin/customers/${id}`),
  create: (data) => request('/admin/customers', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/admin/customers/${id}`, { method: 'PUT', body: JSON.stringify(data) })
};

export const accountProfileApi = {
  get: () => request('/account/profile')
};

export const copilotApi = {
  // La pianificazione non scrive dati: può essere ripetuta in sicurezza dopo
  // una breve interruzione del tunnel. L'esecuzione, invece, non viene ritentata.
  plan: (data) => request('/copilot/plan', { method: 'POST', body: JSON.stringify(data), retryOnNetwork: 2 }),
  execute: (data) => request('/copilot/execute', { method: 'POST', body: JSON.stringify(data) })
};

export const qNegozioApi = {
  status: () => request('/qnegozio/status'),
  agenda: (from, to, signal) => request(`/qnegozio/agenda?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`, {signal}),
  link: (patientId, customerId) => request(`/qnegozio/patients/${patientId}/link`, {method:'PUT',body:JSON.stringify({customerId})})
};

export const dietTemplatesApi = {
 getAll: () => request('/diet-templates'),
 create: (data) => request('/diet-templates',{method:'POST',body:JSON.stringify(data)}),
 archive: (id) => request(`/diet-templates/${id}`,{method:'DELETE'})
};
export const dietDeliveriesApi = {
 getAll: (patientId) => request(`/diet-deliveries?patientId=${encodeURIComponent(patientId)}`),
 create: (planId) => request('/diet-deliveries',{method:'POST',body:JSON.stringify({planId})})
};

export const postVisitTasksApi = {
  getAll: ({ status, patientId, limit = 80 } = {}) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (status) params.set('status', status);
    if (patientId) params.set('patientId', patientId);
    return request(`/post-visit-tasks?${params}`);
  },
  prepare: data => request('/post-visit-tasks/prepare', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/post-visit-tasks/${id}`, { method: 'PUT', body: JSON.stringify(data) })
};

const api = { patientsApi, devicesApi, measurementsApi, visitsApi, sistemaTsApi, dietPlansApi, dietProtocolsApi, foodsApi, foodRecipesApi, appointmentsApi, adminCustomersApi, accountProfileApi, copilotApi, qNegozioApi, dietTemplatesApi, dietDeliveriesApi, postVisitTasksApi };

export default api;

export const healthcareInvoicesApi = {
  list: visitId => request(`/healthcare-invoices${visitId ? `?visitId=${encodeURIComponent(visitId)}` : ''}`),
  settings: () => request('/healthcare-invoices/settings'),
  saveSettings: data => request('/healthcare-invoices/settings', {method:'PUT', body:JSON.stringify(data)}),
  saveDraft: data => request('/healthcare-invoices/draft', {method:'POST', body:JSON.stringify(data)}),
  issue: id => request(`/healthcare-invoices/${id}/issue`, {method:'POST'}),
  pay: (id, data) => request(`/healthcare-invoices/${id}/payment`, {method:'POST', body:JSON.stringify(data)}),
  process: id => request(`/healthcare-invoices/${id}/process-ts`, {method:'POST'}),
  savePrintDetails: (id, data) => request(`/healthcare-invoices/${id}/print-details`, {method:'PUT', body:JSON.stringify(data)}),
  pdf: id => request(`/healthcare-invoices/${id}/pdf`, {responseType:'blob'}),
  register: year => request(`/healthcare-invoices/register?year=${year}`, {responseType:'blob'})
};
